import { COOKIE_EXPIRY_MS } from './cookie';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Master switch — set false to disable ALL IndexedDB API caching (broker + i18next).
 */
export const API_CACHE_MASTER_ENABLED = true;

/**
 * When true, broker + i18next IndexedDB caches are cleared on app load when
 * releaseVersion changes (public/version.js is regenerated on each npm run build).
 */
export const REFRESH_CACHE_ON_BUILD = true;

/**
 * TTL strategies:
 * - session: expires with auth cookie (~2 days); cache key includes session (token / engine-iframe)
 * - days:    fixed calendar TTL; use ttlDays (not tied to auth token)
 * - ms:      fixed TTL in milliseconds; use ttlMs
 */
export const CACHE_TTL_TYPE = {
  SESSION: 'session',
  DAYS: 'days',
  MS: 'ms',
};

/**
 * Cache scope (cache key strategy):
 * - session:  key includes auth session (invalidates on relogin / engine token rotation workaround)
 * - reference: key is eventType + request payload only (shared across sessions)
 */
export const CACHE_SCOPE = {
  SESSION: 'session',
  REFERENCE: 'reference',
};

/**
 * Broker event APIs cached via HttpClient.custom / accountAPI fetchCachedEvent.
 *
 * To DISABLE caching for an API  → set enabled: false (or remove the entry)
 * To ADD a new API              → add an entry with eventType as the key
 * To change TTL                 → set ttlType + ttlDays (or ttlMs)
 *
 * @example
 * GET_MY_NEW_API: {
 *   enabled: true,
 *   ttlType: CACHE_TTL_TYPE.DAYS,
 *   ttlDays: 7,
 *   scope: CACHE_SCOPE.REFERENCE,
 * },
 */
export const BROKER_API_CACHE_CONFIG = {
  GET_KXUSER: {
    enabled: false,
    ttlType: CACHE_TTL_TYPE.SESSION,
    scope: CACHE_SCOPE.SESSION,
  },
  GET_ENTITIES: {
    enabled: false,
    ttlType: CACHE_TTL_TYPE.SESSION,
    scope: CACHE_SCOPE.SESSION,
  },
  GET_TEAM_MEMBERS: {
    enabled: false,
    ttlType: CACHE_TTL_TYPE.SESSION,
    scope: CACHE_SCOPE.SESSION,
  },
  GET_CHANNEL_CONNECTIONS: {
    enabled: false,
    ttlType: CACHE_TTL_TYPE.SESSION,
    scope: CACHE_SCOPE.SESSION,
  },
  GET_VOLUNTARY_PGA: {
    enabled: true,
    ttlType: CACHE_TTL_TYPE.SESSION,
    scope: CACHE_SCOPE.SESSION,
  },
  GET_DOCUMENT_TYPES: {
    enabled: true,
    ttlType: CACHE_TTL_TYPE.DAYS,
    ttlDays: 2,
    scope: CACHE_SCOPE.REFERENCE,
  },
  GET_CURRENCY_CODE: {
    enabled: true,
    ttlType: CACHE_TTL_TYPE.DAYS,
    ttlDays: 2,
    scope: CACHE_SCOPE.REFERENCE,
  },
  GET_CARRIER_CODE: {
    enabled: true,
    ttlType: CACHE_TTL_TYPE.DAYS,
    ttlDays: 2,
    scope: CACHE_SCOPE.REFERENCE,
  },
};

/**
 * i18next translation bundles (i18n.stage.klearnow.com / I18NEXT_API_PATH).
 *
 * Set enabled: false to always fetch translations from the network.
 * Adjust ttlDays to control how long cached namespaces are kept.
 */
export const I18NEXT_CACHE_CONFIG = {
  enabled: true,
  ttlDays: 7,
  /** Translation namespaces to preload from cache on app boot */
  namespaces: [
    'default',
    'header',
    'parties',
    'lineItem',
    'notes',
    'valueBuildUp',
    'state_text',
    'transport',
  ],
};

function getBrokerApiEntry(eventType) {
  if (!eventType) return null;
  return BROKER_API_CACHE_CONFIG[eventType] || null;
}

export function isBrokerApiCacheEnabled(eventType) {
  if (!API_CACHE_MASTER_ENABLED) return false;
  const entry = getBrokerApiEntry(eventType);
  return Boolean(entry?.enabled);
}

export function getBrokerApiCacheTtlMs(eventType) {
  const entry = getBrokerApiEntry(eventType);
  if (!entry?.enabled) return 0;

  switch (entry.ttlType) {
    case CACHE_TTL_TYPE.SESSION:
      return COOKIE_EXPIRY_MS;
    case CACHE_TTL_TYPE.MS:
      return entry.ttlMs || 0;
    case CACHE_TTL_TYPE.DAYS:
    default:
      return (entry.ttlDays ?? 2) * DAY_MS;
  }
}

export function isSessionScopedBrokerApi(eventType) {
  return getBrokerApiEntry(eventType)?.scope === CACHE_SCOPE.SESSION;
}

export function getEnabledBrokerApiEventTypes() {
  if (!API_CACHE_MASTER_ENABLED) return [];
  return Object.entries(BROKER_API_CACHE_CONFIG)
    .filter(([, cfg]) => cfg.enabled)
    .map(([eventType]) => eventType);
}

export function isI18nextCacheEnabled() {
  return API_CACHE_MASTER_ENABLED && Boolean(I18NEXT_CACHE_CONFIG.enabled);
}

export function getI18nextCacheTtlMs() {
  if (!isI18nextCacheEnabled()) return 0;
  return (I18NEXT_CACHE_CONFIG.ttlDays ?? 7) * DAY_MS;
}

export function getI18nextNamespaces() {
  return I18NEXT_CACHE_CONFIG.namespaces || [];
}

/** @deprecated Use getEnabledBrokerApiEventTypes / isBrokerApiCacheEnabled */
export const SESSION_CACHE_EVENT_TYPES = new Set(
  Object.entries(BROKER_API_CACHE_CONFIG)
    .filter(([, cfg]) => cfg.enabled && cfg.scope === CACHE_SCOPE.SESSION)
    .map(([eventType]) => eventType)
);

/** @deprecated Use BROKER_API_CACHE_CONFIG */
export const FIXED_TTL_EVENT_TYPES = new Set(
  Object.entries(BROKER_API_CACHE_CONFIG)
    .filter(([, cfg]) => cfg.enabled && cfg.scope === CACHE_SCOPE.REFERENCE)
    .map(([eventType]) => eventType)
);

/** @deprecated Use getEnabledBrokerApiEventTypes */
export const CACHED_EVENT_TYPES = new Set(getEnabledBrokerApiEventTypes());

/** @deprecated Use getI18nextCacheTtlMs */
export const I18NEXT_TRANSLATION_TTL_MS = getI18nextCacheTtlMs();
